import { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@tellescope/ui";
import { InboxThreadList, InboxThreadListHeader } from "./components";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getInboxView } from "./utils";

type ComposeView = "none" | "chat" | "email" | "sms";

export const InboxThreadAndConversationPage = () => {
  const { t } = useTranslation("inbox");
  const location = useLocation();
  const view = getInboxView(location);

  // Compose state (kept local since it's UI-specific)
  const [composeView, setComposeView] = useState<ComposeView>("none");
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [, setPendingClose] = useState(false);

  const handleSaveDraft = () => {
    try {
      const draftKey = `inbox-draft-${composeView}-${Date.now()}`;
      const draftData = {
        type: composeView,
        timestamp: new Date().toISOString(),
        savedAt: new Date().toLocaleString(),
      };

      localStorage.setItem(draftKey, JSON.stringify(draftData));
    } catch (error) {
      console.warn("Failed to save draft to localStorage:", error);
    }

    setShowDraftDialog(false);
    setPendingClose(false);
    setComposeView("none");
  };

  const handleDiscardDraft = () => {
    setShowDraftDialog(false);
    setPendingClose(false);
    setComposeView("none");
  };

  const handleCancelClose = () => {
    setShowDraftDialog(false);
    setPendingClose(false);
  };

  if (!view) {
    return <Navigate to="/messaging/inbox" />;
  }

  return (
    <div className="flex bg-background h-full w-full overflow-hidden">
      {/* Left Panel - Thread List */}
      <div className="flex flex-col border-r h-full w-[22rem] flex-shrink-0">
        <InboxThreadListHeader />

        <div className="flex-1 flex flex-col overflow-hidden">
          <InboxThreadList />
        </div>
      </div>

      {/* Right Panel - Conversation or Compose - Handled by router */}
      <div className="flex flex-col h-full flex-1 ">
        <Outlet />
      </div>

      {/* Draft Save Confirmation Dialog */}
      <Dialog open={showDraftDialog} onOpenChange={() => {}}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Save as Draft?")}</DialogTitle>
            <DialogDescription>
              {t("You have unsaved changes. Would you like to save this message as a draft before closing?")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleDiscardDraft}>
              {t("Discard")}
            </Button>
            <Button variant="outline" onClick={handleCancelClose}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleSaveDraft}>{t("Save as Draft")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
